import {Component, ComponentRef, DestroyRef, EventEmitter, inject, input, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef} from '@angular/core';
import {TreeData, TreeNode} from '../../../services/treeview.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TreeviewPageComponent} from './tree-page/treeview-page.component';
import {ReplaySubject} from 'rxjs';

@Component({
    selector: 'treeview', templateUrl: './treeview.component.html', styleUrls: ['./treeview.component.scss'],
})
export class TreeviewComponent implements OnInit, OnDestroy {
    @Input() treeData$: ReplaySubject<TreeData> | null = null;
    @Input() highlightObject: EventEmitter<string> | null = null;
    @Input() nrElementsPerPage: number = 50;
    @Input() deleteNode: EventEmitter<any> | null = null;
    @Input() addedNewlyCreatedNode: EventEmitter<{ node: TreeNode, parentObjID: string }> | null = null;
    @Input() hideNavBar: boolean = false;

    @Output() objectSelected = new EventEmitter<any>();
    @Output() searchObjNotFound = new EventEmitter<any>();
    treeNodeAdded = new EventEmitter<TreeNode[]>();
    showStatusColorInTree = input.required<boolean>();

    pageData = new Map<number, TreeNode[]>;
    activePage = 0;
    selectedNode: TreeNode | null = null;
    @ViewChild('vc', {read: ViewContainerRef, static: true}) vc: ViewContainerRef | undefined;
    private treeData: TreeData | null = null;
    private destroyRef = inject(DestroyRef);
    private pages: Map<number, ComponentRef<TreeviewPageComponent>> = new Map<number, ComponentRef<TreeviewPageComponent>>();

    constructor() {
    }

    ngOnDestroy(): void {
        this.pages.forEach(c => c?.destroy());
    }

    ngOnInit(): void {
        this.treeData$!.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
            this.treeData = data;
            this.pageData = new Map<number, TreeNode[]>;
            this.pages.forEach(c => c?.destroy());
            this.pages = new Map<number, ComponentRef<TreeviewPageComponent>>();
            for (let i = 0; i * this.nrElementsPerPage < this.treeData!.objects!.length; i++) {
                this.pageData.set(i, this.treeData!.objects!.slice(i * this.nrElementsPerPage, (i + 1) * this.nrElementsPerPage));
            }
            this.navigatePage(this.activePage);
        });

        this.highlightObject?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (id: string) => {
            const foundNode = this.treeData?.search!.get(id);
            if (!foundNode) {
                return this.searchObjNotFound.emit(id);
            }

            this.findInTreeAndNavigate(foundNode, [foundNode], id).then(res => {
                if (this.selectedNode) {
                    this.selectedNode.element!.selected = false;
                }
                requestAnimationFrame(() => {
                    this.navigatePage(res.activePage);
                    requestAnimationFrame(() => {
                        this.selectNode(res.nodes);
                    });
                });

            }).catch((err) => {
                console.log(err);
            });
        });

        this.addedNewlyCreatedNode?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (addData: { node: TreeNode, parentObjID: string }) => {
            const parent = this.treeData?.search?.get(addData.parentObjID);
            if (parent) {
                addData.node.parent = parent;
                addData.node.pid = parent.id;
                addData.node.depth = parent.depth + 1;
                parent.hasChild = true;
                parent.children.push(addData.node);
                parent.element?.addChild(addData.node);
            } else {
                addData.node.parent = undefined;
                addData.node.pid = 1;
                addData.node.depth = 0;
                this.pageData?.get(this.pageData.size - 1)?.push(addData.node);
                this.treeData?.objects?.push(addData.node);
            }
            this.treeData?.search?.set(addData.node.objectID!, addData.node);
            this.pages.get(this.pageData.size - 1)?.instance?.regen();
            setTimeout(() => this.highlightObject?.emit(addData.node.objectID), 200);
        });

        this.deleteNode?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (id: string) => {
            const node = this.treeData?.search?.get(id);

            if (node?.parent) {
                const i = node.parent.children.findIndex(n => n.objectID == node.objectID);
                node.parent.children.splice(i, 1);
                node.parent.element?.removeChild(node.objectID);

                if (node.parent.children.length == 0) {
                    node.parent.hasChild = false;
                }

                setTimeout(() => this.highlightObject?.emit(node.parent?.objectID), 200);
            } else {
                const i = this.treeData!.objects!.findIndex(n => n.objectID == node!.objectID);
                this.treeData!.objects!.splice(i, 1);
                const targetPage = Math.floor(i / this.nrElementsPerPage);
                const iPages = this.pageData.get(targetPage)!.findIndex(n => n.objectID == node!.objectID);
                this.pageData.get(targetPage)!.splice(iPages, 1);
            }

            this.treeData?.search?.delete(node?.objectID!);
            this.pages.get(this.pageData.size - 1)?.instance?.removeNodeFromCache(node?.objectID!);
            node!.element!.nodeElement!.nativeElement.style.display = 'none';
        });

        this.treeNodeAdded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((treeNodes) => {
            treeNodes.forEach(node => {
                this.treeData?.search?.set(node.objectID!, node);
            });
        });

        this.navigatePage(this.activePage);
    }

    selectNode(nodes: TreeNode[]): void {
        const node = nodes.pop();
        if (!node) {
            return;
        }

        if (nodes.length !== 0) {
            setTimeout(() => {
                node.element!.showChildren();
                this.selectNode(nodes);
            });
        } else {
            setTimeout(() => node.element!.selectObject());
        }
    }

    navigatePage(n: number): void {
        if (n > -1 && this.pageData.size > n) {
            this.vc!.detach();

            if (this.pages.has(n)) {
                this.vc!.insert(this.pages.get(n)!.hostView);
            } else {
                const page: ComponentRef<TreeviewPageComponent> = this.createPage(this.pageData.get(n)!);
                page.setInput('treeNodeChildrenLoaded', this.treeNodeAdded);
                this.pages.set(n, page);
            }
            this.activePage = n;
        }
    }

    private async findInTreeAndNavigate(foundNode: TreeNode, nodesToOpen: TreeNode[], id): Promise<{ activePage: number, nodes: TreeNode[] }> {
        if (foundNode.parent) {
            nodesToOpen.push(foundNode.parent);
            return this.findInTreeAndNavigate(foundNode.parent!, nodesToOpen, id);
        } else {
            // is top node
            const i = this.treeData?.objects?.findIndex((o) => foundNode.objectID == o.objectID);
            let n = 0;
            if (i && i > 0) {
                n = Math.floor(i / this.nrElementsPerPage);
            }
            return {activePage: n, nodes: nodesToOpen};
        }
    }

    private createPage(pageData: TreeNode[]): ComponentRef<TreeviewPageComponent> {
        const treeViewPage: ComponentRef<TreeviewPageComponent> = this.vc!.createComponent(TreeviewPageComponent);

        treeViewPage.setInput('objectSelected', this.objectSelected);
        treeViewPage.setInput('treeNodes', pageData);
        treeViewPage.setInput('treeNodeChildrenLoaded', this.treeNodeAdded);
        treeViewPage.setInput('showStatusColorInTree', this.showStatusColorInTree());

        return treeViewPage;
    }
}
